import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { PortalState, Company, Department, Gate, Document, IntelligenceSignal, Decision, WaveStatus, ActionItem, Incident, TeamCapacity, Principle, AuthorityRule, ComplianceStatus, Risk, ADREntry, MatchReport, NotificationCard, CompletedEpic, NewsfeedItem, PostIt, CanvasChatMessage, LeanCanvasBlock, OntologyEntity, OntologyRelationship, KnowledgeAuditEntry, FounderControl, EntityType, RelationshipType, SettingsConfig, BurnRateStatus, BacklogEpic, ArchFact, LucasChatMessage, ApprovalItem, GovernorDecisionItem, BoardPersona, BoardDecision, Toast, AlertThresholds, DiscordLevel, BlockedSkill, MandateArchetype, InterventionHistoryItem, InterventionHistoryResponse } from '../types';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

/** Wrapper that always sends the httpOnly zpc_token cookie (needed for JWT auth). */
const apiFetch = (url: string, opts: RequestInit = {}) =>
  fetch(url, { credentials: 'include', ...opts });

interface PortalStore extends PortalState {
  // View management
  navigateTo: (view: string) => void;

  // Company operations
  loadCompany: () => Promise<void>;
  updateCompany: (company: Partial<Company>) => void;

  // Department operations
  loadDepartments: () => Promise<void>;
  selectDepartment: (dept: Department) => void;
  clearDepartmentSelection: () => void;

  // Gate operations
  loadGates: () => Promise<void>;
  selectGate: (gate: Gate) => void;
  approveGate: (gateId: string) => Promise<void>;
  rejectGate: (gateId: string, reason: string) => Promise<void>;
  reviseGate: (gateId: string, notes: string) => Promise<void>;

  // Document operations
  loadDocuments: () => Promise<void>;
  selectDocument: (doc: Document) => void;
  updateDocument: (docId: string, content: string) => Promise<void>;

  // Intelligence operations
  loadSignals: () => Promise<void>;
  dismissSignal: (signalId: string) => void;

  // Decision operations
  loadDecisions: () => Promise<void>;
  recordDecision: (decision: Omit<Decision, 'id' | 'madeAt' | 'madeBy'>) => Promise<void>;

  // Operations layer operations
  loadWaveStatus: () => Promise<void>;
  loadActionItems: () => Promise<void>;
  loadIncidents: () => Promise<void>;
  loadTeamCapacity: () => Promise<void>;
  updateActionItemStatus: (id: string, status: string) => void;

  // Governance layer operations
  loadPrinciples: () => Promise<void>;
  loadAuthorityMatrix: () => Promise<void>;
  loadComplianceStatuses: () => Promise<void>;
  loadRisks: () => Promise<void>;
  loadADRHistory: () => Promise<void>;

  // Command Centre operations
  loadMatchReport: () => Promise<void>;
  loadNotifications: () => Promise<void>;
  dismissNotification: (id: string) => Promise<void>;
  loadCompletedEpics: () => Promise<void>;
  acknowledgeCompletedEpic: (id: string) => Promise<void>;
  loadNewsfeed: (category?: string) => Promise<void>;
  setNewsfeedCategory: (category: string) => void;

  // Canvas workspace
  loadCanvas: () => Promise<void>;
  addFounderPostIt: (block: LeanCanvasBlock, content: string) => Promise<void>;
  queueCofounderPostIts: (suggestions: Array<{ block: LeanCanvasBlock; content: string }>) => Promise<void>;
  removePostIt: (id: string) => Promise<void>;
  sendCanvasMessage: (message: string) => Promise<void>;
  loadCanvasChat: () => Promise<void>;

  // Knowledge Room
  loadKnowledgeEntities: () => Promise<void>;
  loadKnowledgeRelationships: () => Promise<void>;
  loadKnowledgeAudit: () => Promise<void>;
  createEntity: (entity: { entity_type: EntityType; label: string; confidence: number; source: string; control: FounderControl; is_pending: boolean }) => Promise<void>;
  updateEntity: (id: string, entity: { entity_type: EntityType; label: string; confidence: number; source: string; control: FounderControl; is_pending: boolean }) => Promise<void>;
  deleteEntity: (id: string) => Promise<void>;
  setEntityControl: (id: string, control: FounderControl) => Promise<void>;
  createRelationship: (rel: { from_entity: string; to_entity: string; relationship_type: RelationshipType; strength: number }) => Promise<void>;
  deleteRelationship: (id: string) => Promise<void>;
  selectKnowledgeEntity: (entity: OntologyEntity | undefined) => void;

  // Settings + Burn Rate
  loadSettings: () => Promise<void>;
  updateSettings: (patch: Partial<SettingsConfig>) => Promise<void>;
  loadBurnRate: () => Promise<void>;
  setDepartmentPause: (department: string, paused: boolean) => Promise<void>;

  // ZPC CRUD operations
  loadBacklogEpics: (status?: string) => Promise<void>;
  createEpic: (title: string, description: string, wsjf: number) => Promise<BacklogEpic>;
  updateEpicStatus: (epicId: string, status: string) => Promise<void>;
  loadArchFacts: () => Promise<void>;
  addArchFact: (domain: string, key: string, value: string, confidence?: string) => Promise<ArchFact>;
  loadLucasChat: () => Promise<void>;
  sendLucasMessage: (message: string) => Promise<void>;
  loadApprovals: () => Promise<void>;
  decideApproval: (nmId: string, decision: 'approved' | 'rejected', reason: string) => Promise<void>;
  loadGovernorDecisions: () => Promise<void>;
  loadBoardDecisions: () => Promise<void>;

  // Board advisor personas
  loadBoardPersonas: () => Promise<void>;

  // Error management
  setError: (error: string | undefined) => void;
  clearError: () => void;

  // Toast notifications
  addToast: (message: string, type?: Toast['type']) => void;
  removeToast: (id: string) => void;

  // New founder write operations
  triggerBriefingRegeneration: () => Promise<void>;
  loadMandateArchetype: () => Promise<void>;
  setMandateArchetype: (archetype: string) => Promise<void>;
  loadBlockedSkills: () => Promise<void>;
  blockSkill: (skillName: string, reason: string) => Promise<void>;
  unblockSkill: (skillName: string) => Promise<void>;
  loadAlertThresholds: () => Promise<void>;
  setAlertThresholds: (thresholds: AlertThresholds) => Promise<void>;
  loadDiscordLevel: () => Promise<void>;
  setDiscordLevel: (level: DiscordLevel) => Promise<void>;
  resolveBoardDecision: (decisionId: string) => Promise<void>;

  // Gate write operations (wired to API)
  approveGateApi: (gateId: string) => Promise<void>;
  rejectGateApi: (gateId: string, reason: string) => Promise<void>;

  // Founder interventions
  applyIntervention: (action: string, body: Record<string, unknown>) => Promise<Record<string, unknown>>;
  loadInterventionHistory: (epicId?: string, actionType?: string) => Promise<void>;

  // Spatial UI (epic-371)
  focusDepartment: (deptId: string) => void;
  unfocusDepartment: () => void;
  setSavedCameraPosition: (pos: { x: number; y: number; zoom: number }) => void;
}

const initialState: PortalState = {
  currentView: 'command-centre',
  company: null,
  departments: [],
  gates: [],
  documents: [],
  signals: [],
  decisions: [],
  actionItems: [],
  incidents: [],
  teamCapacity: [],
  principles: [],
  authorityMatrix: [],
  complianceStatuses: [],
  risks: [],
  adrHistory: [],
  matchReport: null,
  notifications: [],
  completedEpics: [],
  newsfeed: [],
  newsfeedCategory: '',
  isLoading: false,
  knowledgeEntities: [],
  knowledgeRelationships: [],
  knowledgeAudit: [],
  canvasPostIts: [],
  canvasChat: [],
  canvasChatLoading: false,
  settings: null,
  burnRate: null,
  backlogEpics: [],
  archFacts: [],
  lucasChat: [],
  lucasChatLoading: false,
  approvals: [],
  governorDecisions: [],
  boardDecisions: [],
  boardPersonas: [],
  toasts: [],
  alertThresholds: null,
  discordLevel: null,
  blockedSkills: [],
  mandateArchetype: null,
  interventionHistory: [],
  focusedDepartment: null,
  isOverlayOpen: false,
  savedCameraPosition: { x: 0, y: 0, zoom: 1 },
};

export const usePortalStore = create<PortalStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      navigateTo: (view: string) => {
        set({ currentView: view });
      },

      loadCompany: async () => {
        set({ isLoading: true });
        try {
          const [identityRes, overviewRes] = await Promise.all([
            apiFetch(`${API_BASE}/api/zpc/identity`),
            apiFetch(`${API_BASE}/api/zpc/overview`),
          ]);
          const identity = identityRes.ok ? await identityRes.json() : null;
          const overview = overviewRes.ok ? await overviewRes.json() : null;
          set({
            company: {
              id: identity?.zpc_id ?? 'marvis-labs',
              name: identity?.zpc_name ?? 'Marvis Labs',
              vision: 'Operating System for Autonomous Companies',
              mission: identity
                ? `${identity.cofounder_name} co-founded with ${identity.founder_name} — ${identity.domain}`
                : 'AI co-founder platform for European B2B SaaS',
              values: ['Innovation', 'Integrity', 'Impact', 'Intelligence'],
              founded: new Date('2024-01-01'),
              health: overview?.health_score ?? 0,
              tokenBurnRate: overview?.tokens_per_week ?? 0,
              costPerWeek: overview?.cost_per_week_usd ?? 0,
              contextUsagePct: overview?.context_usage_pct ?? 0,
            },
            isLoading: false,
          });
        } catch (error) {
          set({ error: (error as Error).message, isLoading: false });
        }
      },

      updateCompany: (updates: Partial<Company>) => {
        const current = get().company;
        if (current) {
          set({ company: { ...current, ...updates } });
        }
      },

      loadDepartments: async () => {
        set({ isLoading: true });
        try {
          const [overviewRes, skillRunsRes] = await Promise.all([
            apiFetch(`${API_BASE}/api/zpc/overview`),
            apiFetch(`${API_BASE}/api/zpc/skill-runs?limit=50`),
          ]);
          const overview = overviewRes.ok ? await overviewRes.json() : {};
          const skillRuns: Record<string, unknown>[] = skillRunsRes.ok ? await skillRunsRes.json() : [];
          const inProgress: number = overview.in_progress ?? 0;
          const todo: number = overview.todo ?? 0;
          const health: number = overview.health_score ?? 0;
          const costPerWeek: number = overview.cost_per_week_usd ?? 0;
          const skillNames = new Set(skillRuns.map(r => r.skill_name as string).filter(Boolean));
          set({
            departments: [
              {
                id: 'dept-eng',
                name: 'Engineering',
                charter: 'Build reliable, tested software',
                skillCount: Math.max(skillNames.size, 3),
                queueDepth: todo,
                health,
                costPerWeek: Math.round(costPerWeek * 1000),
                workflowCount: inProgress,
                skills: [],
              },
            ],
            isLoading: false,
          });
        } catch (error) {
          set({ error: (error as Error).message, isLoading: false });
        }
      },

      selectDepartment: (dept: Department) => {
        set({ selectedDepartment: dept });
      },

      clearDepartmentSelection: () => {
        set({ selectedDepartment: undefined });
      },

      loadGates: async () => {
        set({ isLoading: true });
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/gates`);
          const raw: Record<string, unknown>[] = res.ok ? await res.json() : [];
          const gates: Gate[] = raw.map(g => ({
            id: g.id as string,
            epicId: g.epicId as string | undefined,
            phase: g.phase as string,
            artifact: {
              ...(g.artifact as Record<string, unknown>),
              createdAt: new Date((g.artifact as Record<string, unknown>).createdAt as string),
              updatedAt: new Date((g.artifact as Record<string, unknown>).updatedAt as string),
            } as Gate['artifact'],
            createdAt: new Date(g.createdAt as string),
            status: (g.status ?? 'pending') as Gate['status'],
            qualityScore: g.qualityScore as Gate['qualityScore'],
          }));
          set({ gates, isLoading: false });
        } catch (error) {
          set({ error: (error as Error).message, isLoading: false });
        }
      },

      selectGate: (gate: Gate) => {
        set({ selectedGate: gate });
      },

      approveGate: async (gateId: string) => {
        const gates = get().gates.map(g =>
          g.id === gateId ? { ...g, status: 'approved' as const } : g
        );
        set({ gates });
      },

      rejectGate: async (gateId: string, reason: string) => {
        const gates = get().gates.map(g =>
          g.id === gateId ? { ...g, status: 'rejected' as const } : g
        );
        set({ gates });
      },

      reviseGate: async (gateId: string, notes: string) => {
        const gates = get().gates.map(g =>
          g.id === gateId ? { ...g, status: 'revised' as const } : g
        );
        set({ gates });
      },

      loadDocuments: async () => {
        set({ isLoading: true });
        try {
          set({
            documents: [
              {
                id: 'doc-vision',
                name: 'VISION.md',
                layer: 'constitution',
                path: '/CONSTITUTION/VISION.md',
                content: 'To become the leading platform for autonomous company operations...',
                owner: 'Board',
                lastReview: new Date('2026-01-15'),
                reviewCycle: 'Annual',
                version: 3,
                referencedBy: ['STRATEGY.md', 'DEPARTMENTS/engineering/CHARTER.md'],
                cascadesTo: ['STRATEGY.md', 'MISSION.md'],
              },
            ],
            isLoading: false,
          });
        } catch (error) {
          set({ error: (error as Error).message, isLoading: false });
        }
      },

      selectDocument: (doc: Document) => {
        set({ selectedDocument: doc });
      },

      updateDocument: async (docId: string, content: string) => {
        const documents = get().documents.map(d =>
          d.id === docId ? { ...d, content, version: d.version + 1, updatedAt: new Date() } : d
        );
        set({ documents });
      },

      loadSignals: async () => {
        set({
          signals: [
            {
              id: 'signal-1',
              category: 'tech',
              title: 'Claude 4.6 Opus Released',
              description: 'New model with improved reasoning and longer context window',
              severity: 'medium',
              source: 'Anthropic',
              detected: new Date(Date.now() - 24 * 60 * 60 * 1000),
              relevantTo: ['engineering', 'product'],
            },
          ],
        });
      },

      dismissSignal: (signalId: string) => {
        const signals = get().signals.filter(s => s.id !== signalId);
        set({ signals });
      },

      loadDecisions: async () => {
        set({
          decisions: [
            {
              id: 'dec-1',
              title: 'Use React for Portal UI',
              description: 'Chose React over Vue for better TypeScript support',
              context: 'Need modern, maintainable web UI framework',
              alternatives: ['Vue 3', 'Svelte', 'Angular'],
              decision: 'React 18 + TypeScript',
              rationale: 'Best ecosystem, type safety, large team familiarity',
              madeAt: new Date('2026-03-28'),
              madeBy: 'lucas',
              status: 'implemented',
              impacts: ['Portal architecture', 'Frontend development pace'],
            },
          ],
        });
      },

      recordDecision: async (decision: Omit<Decision, 'id' | 'madeAt' | 'madeBy'>) => {
        const newDecision: Decision = {
          ...decision,
          id: `dec-${Date.now()}`,
          madeAt: new Date(),
          madeBy: 'lucas', // Replace with actual user
        };
        set({ decisions: [...get().decisions, newDecision] });
      },

      // Operations Layer
      loadWaveStatus: async () => {
        try {
          const [backlogRes, overviewRes] = await Promise.all([
            apiFetch(`${API_BASE}/api/zpc/backlog?status=in_progress&limit=20`),
            apiFetch(`${API_BASE}/api/zpc/overview`),
          ]);
          const inProgressEpics = backlogRes.ok ? await backlogRes.json() : [];
          const overview = overviewRes.ok ? await overviewRes.json() : {};
          const done: number = overview.done ?? 0;
          const inProgress: number = overview.in_progress ?? 0;
          const todo: number = overview.todo ?? 0;
          const total: number = overview.total_epics ?? 1;
          const progressPct = Math.round((done / total) * 100);
          const waveStatus: WaveStatus = {
            number: done,
            phase: `${done} delivered · ${inProgress} in progress · ${todo} in queue`,
            startDate: new Date('2024-01-01'),
            estimatedCompletion: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            progressPercentage: progressPct,
            status: inProgress > 0 ? 'active' : 'planning',
            epics: inProgressEpics.map((e: Record<string, unknown>) => ({
              id: e.id as string,
              number: parseInt((e.id as string).replace('epic-', ''), 10) || 0,
              name: e.title as string,
              status: 'in-progress' as const,
              progressPercentage: 50,
              owner: 'lucas',
              dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
              tasks: [],
            })),
            blockers: [],
          };
          set({ waveStatus });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      loadActionItems: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/backlog?status=todo&limit=8`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const epics: Record<string, unknown>[] = await res.json();
          const actionItems: ActionItem[] = epics.map(e => ({
            id: e.id as string,
            title: e.title as string,
            description: ((e.description as string | null) ?? '').slice(0, 200),
            owner: 'lucas',
            dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            status: 'pending' as const,
            priority: (e.wsjf as number) >= 20 ? 'critical' : (e.wsjf as number) >= 15 ? 'high' : 'medium',
          }));
          set({ actionItems });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      loadIncidents: async () => {
        try {
          const incidents: Incident[] = [
            {
              id: 'incident-1',
              title: 'Portal Load Time > 2s',
              description: 'Initial load takes 2.5s, need to optimize',
              severity: 'high',
              status: 'open',
              discoveredAt: new Date('2026-04-02'),
              assignedTo: 'engineering-team',
            },
          ];
          set({ incidents });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      loadTeamCapacity: async () => {
        try {
          const capacity: TeamCapacity[] = [
            {
              agentId: 'agent-architect',
              name: 'Architect Agent',
              role: 'Architecture & Design',
              currentCapacity: 65,
              assignedTasks: 3,
              completionRate: 87,
            },
            {
              agentId: 'agent-implement',
              name: 'Implement Agent',
              role: 'Code Implementation',
              currentCapacity: 45,
              assignedTasks: 5,
              completionRate: 92,
            },
            {
              agentId: 'agent-verify',
              name: 'Verify Agent',
              role: 'Quality & Testing',
              currentCapacity: 78,
              assignedTasks: 2,
              completionRate: 88,
            },
          ];
          set({ teamCapacity: capacity });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      updateActionItemStatus: (id: string, status: string) => {
        const actionItems = get().actionItems.map(item =>
          item.id === id ? { ...item, status: status as any } : item
        );
        set({ actionItems });
      },

      // Governance Layer
      loadPrinciples: async () => {
        try {
          const principles: Principle[] = [
            {
              id: 'principle-1',
              title: 'Serve First',
              description: 'Wealth and ownership are oxygen for the business, not the goal',
              category: 'values',
              complianceScore: 92,
            },
            {
              id: 'principle-2',
              title: 'Specification Before Build',
              description: 'Specify before you build. The most expensive code is code that solves the wrong problem.',
              category: 'principles',
              complianceScore: 88,
            },
            {
              id: 'principle-3',
              title: 'Ship Fast, Never Broken',
              description: 'Ship incrementally, validate continuously. Quality is structure, not finish.',
              category: 'standards',
              complianceScore: 85,
            },
          ];
          set({ principles });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      loadAuthorityMatrix: async () => {
        try {
          const matrix: AuthorityRule[] = [
            {
              id: 'auth-1',
              role: 'Board',
              authority: 'Final decision authority on all strategic direction',
              decisionTypes: ['Strategy', 'Major investments', 'Policy changes', 'Exceptions'],
            },
            {
              id: 'auth-2',
              role: 'Product Owner',
              authority: 'Decides product features and roadmap',
              decisionTypes: ['Feature prioritization', 'API contracts', 'User experience'],
              constraints: ['Must align with Board strategy'],
            },
            {
              id: 'auth-3',
              role: 'Engineering Lead',
              authority: 'Decides technical implementation',
              decisionTypes: ['Architecture', 'Technology selection', 'Code quality standards'],
              constraints: ['Must meet security policy', 'NASA Power of Ten exceptions need Board approval'],
            },
          ];
          set({ authorityMatrix: matrix });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      loadComplianceStatuses: async () => {
        try {
          const statuses: ComplianceStatus[] = [
            {
              standard: 'GDPR',
              status: 'compliant',
              score: 94,
              lastAudit: new Date('2026-03-15'),
              requirements: ['Data minimization', 'Consent management', 'Right to deletion', 'Privacy by design'],
            },
            {
              standard: 'SOC 2 Type II',
              status: 'in-progress',
              score: 78,
              lastAudit: new Date('2026-02-01'),
              requirements: ['Access controls', 'Encryption', 'Incident response', 'Monitoring'],
            },
            {
              standard: 'ISO 27001',
              status: 'non-compliant',
              score: 62,
              lastAudit: new Date('2025-12-01'),
              requirements: ['Information security policy', 'Asset management', 'Access control', 'Cryptography'],
            },
          ];
          set({ complianceStatuses: statuses });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      loadRisks: async () => {
        try {
          const risks: Risk[] = [
            {
              id: 'risk-1',
              title: 'Founder Overload',
              description: 'Single founder managing too many domains',
              likelihood: 'high',
              impact: 'high',
              status: 'open',
              mitigation: 'Delegate to department leads',
            },
            {
              id: 'risk-2',
              title: 'Data Leakage',
              description: 'Unauthorized access to sensitive company data',
              likelihood: 'medium',
              impact: 'high',
              status: 'mitigating',
              mitigation: 'Encryption, RBAC, audit logs',
            },
            {
              id: 'risk-3',
              title: 'Agent Failure',
              description: 'AI agent produces low-quality work that breaks in production',
              likelihood: 'medium',
              impact: 'high',
              status: 'mitigating',
              mitigation: 'Quality gates, automated testing, human review',
            },
          ];
          set({ risks });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      loadADRHistory: async () => {
        try {
          const adrHistory: ADREntry[] = [
            {
              id: 'adr-1',
              title: 'Use React for Portal UI',
              status: 'accepted',
              context: 'Need modern, maintainable web UI framework',
              decision: 'React 18 + TypeScript + Tailwind CSS',
              consequences: 'Fast development, good ecosystem, easy testing',
              date: new Date('2026-03-28'),
              author: 'lucas',
              alternatives: ['Vue 3', 'Svelte'],
            },
            {
              id: 'adr-2',
              title: 'Game-Like Portal Instead of Enterprise Dashboard',
              status: 'accepted',
              context: 'Target audience is millennial/Gen-Z founders who expect game-like UX',
              decision: 'Implement room metaphor with progression mechanics and achievements',
              consequences: 'Higher engagement, better founder experience, longer implementation',
              date: new Date('2026-03-29'),
              author: 'lucas',
            },
          ];
          set({ adrHistory });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      // Command Centre
      loadMatchReport: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/command-centre/match-report`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const report: MatchReport = {
            sentences: data.sentences ?? ['No report available.', '', ''],
            generatedAt: data.generated_at ?? new Date().toISOString(),
            epicsCompleted: data.epics_completed ?? 0,
            epicsInProgress: data.epics_in_progress ?? 0,
          };
          set({ matchReport: report });
        } catch {
          set({ matchReport: null });
        }
      },

      loadNotifications: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/command-centre/notifications`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: NotificationCard[] = await res.json();
          set({ notifications: data });
        } catch {
          set({ notifications: [] });
        }
      },

      dismissNotification: async (id: string) => {
        try {
          await apiFetch(`${API_BASE}/api/command-centre/notifications/${id}/dismiss`, { method: 'POST' });
          set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) }));
        } catch {
          // best effort dismiss
        }
      },

      loadCompletedEpics: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/command-centre/completed-epics`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const epics: CompletedEpic[] = data.map((e: Record<string, string>) => ({
            id: e.id,
            title: e.title,
            summary: e.summary,
            completedAt: e.completed_at,
          }));
          set({ completedEpics: epics });
        } catch {
          set({ completedEpics: [] });
        }
      },

      acknowledgeCompletedEpic: async (id: string) => {
        try {
          await apiFetch(`${API_BASE}/api/command-centre/completed-epics/${id}/acknowledge`, { method: 'POST' });
          set((s) => ({ completedEpics: s.completedEpics.filter((e) => e.id !== id) }));
        } catch {
          // best effort
        }
      },

      loadNewsfeed: async (category = '') => {
        try {
          const url = category
            ? `${API_BASE}/api/command-centre/newsfeed?category=${category}`
            : `${API_BASE}/api/command-centre/newsfeed`;
          const res = await apiFetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const items: NewsfeedItem[] = data.map((item: Record<string, string>) => ({
            id: item.id,
            category: item.category as NewsfeedItem['category'],
            title: item.title,
            body: item.body,
            source: item.source,
            detectedAt: item.detected_at,
          }));
          set({ newsfeed: items });
        } catch {
          set({ newsfeed: [] });
        }
      },

      setNewsfeedCategory: (category: string) => {
        set({ newsfeedCategory: category });
      },

      // Canvas workspace
      loadCanvas: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/canvas/state`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const raw: PostIt[] = (data.post_its ?? []).map((p: Record<string, string>) => ({
            id: p.id,
            block: p.block as PostIt['block'],
            content: p.content,
            author: p.author as PostIt['author'],
            created_at: p.created_at,
            isAnimating: false,
          }));
          set({ canvasPostIts: raw });
        } catch {
          // Canvas starts blank — not a fatal error
          set({ canvasPostIts: [] });
        }
      },

      addFounderPostIt: async (block: LeanCanvasBlock, content: string) => {
        try {
          const res = await apiFetch(`${API_BASE}/api/canvas/post-its`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ block, content, author: 'founder' }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const postit: PostIt = await res.json();
          set(s => ({ canvasPostIts: [...s.canvasPostIts, { ...postit, isAnimating: false }] }));
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      queueCofounderPostIts: async (suggestions: Array<{ block: LeanCanvasBlock; content: string }>) => {
        const TEMPO_MS = 1800;  // co-founder "thinking" delay between each post-it
        for (const [i, s] of suggestions.entries()) {
          await new Promise(r => setTimeout(r, i === 0 ? 1200 : TEMPO_MS));
          try {
            const res = await apiFetch(`${API_BASE}/api/canvas/post-its`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ block: s.block, content: s.content, author: 'cofounder' }),
            });
            if (!res.ok) continue;
            const postit: PostIt = await res.json();
            // Animate in: add with isAnimating=true, then flip to false after transition
            set(st => ({ canvasPostIts: [...st.canvasPostIts, { ...postit, isAnimating: true }] }));
            await new Promise(r => setTimeout(r, 50));
            set(st => ({
              canvasPostIts: st.canvasPostIts.map(p =>
                p.id === postit.id ? { ...p, isAnimating: false } : p
              ),
            }));
          } catch {
            // best effort
          }
        }
      },

      removePostIt: async (id: string) => {
        try {
          await apiFetch(`${API_BASE}/api/canvas/post-its/${id}`, { method: 'DELETE' });
          set(s => ({ canvasPostIts: s.canvasPostIts.filter(p => p.id !== id) }));
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      sendCanvasMessage: async (message: string) => {
        set(s => ({
          canvasChat: [...s.canvasChat, {
            id: `msg-${Date.now()}`,
            role: 'founder',
            content: message,
            created_at: new Date().toISOString(),
          }],
          canvasChatLoading: true,
        }));
        try {
          const res = await apiFetch(`${API_BASE}/api/canvas/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const cfMsg: CanvasChatMessage = {
            id: data.response.id,
            role: 'cofounder',
            content: data.response.content,
            created_at: data.response.created_at,
            post_it_suggestions: data.post_it_suggestions,
          };
          set(s => ({
            canvasChat: [...s.canvasChat, cfMsg],
            canvasChatLoading: false,
          }));
          // Queue co-founder post-its at human tempo
          if (data.post_it_suggestions?.length) {
            get().queueCofounderPostIts(data.post_it_suggestions);
          }
        } catch (error) {
          set({ error: (error as Error).message, canvasChatLoading: false });
        }
      },

      loadCanvasChat: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/canvas/chat`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: CanvasChatMessage[] = await res.json();
          set({ canvasChat: data });
        } catch {
          set({ canvasChat: [] });
        }
      },

      // Knowledge Room
      loadKnowledgeEntities: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/knowledge-room/entities`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: OntologyEntity[] = await res.json();
          set({ knowledgeEntities: data });
        } catch {
          set({ knowledgeEntities: [] });
        }
      },

      loadKnowledgeRelationships: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/knowledge-room/relationships`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: OntologyRelationship[] = await res.json();
          set({ knowledgeRelationships: data });
        } catch {
          set({ knowledgeRelationships: [] });
        }
      },

      loadKnowledgeAudit: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/knowledge-room/audit`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: KnowledgeAuditEntry[] = await res.json();
          set({ knowledgeAudit: data });
        } catch {
          set({ knowledgeAudit: [] });
        }
      },

      createEntity: async (entity) => {
        const res = await apiFetch(`${API_BASE}/api/knowledge-room/entities`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entity),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const created: OntologyEntity = await res.json();
        set(s => ({ knowledgeEntities: [...s.knowledgeEntities, created] }));
      },

      updateEntity: async (id, entity) => {
        const res = await apiFetch(`${API_BASE}/api/knowledge-room/entities/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entity),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const updated: OntologyEntity = await res.json();
        set(s => ({
          knowledgeEntities: s.knowledgeEntities.map(e => e.id === id ? updated : e),
        }));
      },

      deleteEntity: async (id) => {
        const res = await apiFetch(`${API_BASE}/api/knowledge-room/entities/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        set(s => ({ knowledgeEntities: s.knowledgeEntities.filter(e => e.id !== id) }));
      },

      setEntityControl: async (id, control) => {
        const res = await apiFetch(`${API_BASE}/api/knowledge-room/entities/${id}/control`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ control }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const updated: OntologyEntity = await res.json();
        set(s => ({
          knowledgeEntities: s.knowledgeEntities.map(e => e.id === id ? updated : e),
        }));
      },

      createRelationship: async (rel) => {
        const res = await apiFetch(`${API_BASE}/api/knowledge-room/relationships`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(rel),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const created: OntologyRelationship = await res.json();
        set(s => ({ knowledgeRelationships: [...s.knowledgeRelationships, created] }));
      },

      deleteRelationship: async (id) => {
        const res = await apiFetch(`${API_BASE}/api/knowledge-room/relationships/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        set(s => ({ knowledgeRelationships: s.knowledgeRelationships.filter(r => r.id !== id) }));
      },

      selectKnowledgeEntity: (entity) => {
        set({ selectedKnowledgeEntity: entity });
      },

      // Settings + Burn Rate
      loadSettings: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/settings`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          set({ settings: data.settings as SettingsConfig, burnRate: data.burn_rate as BurnRateStatus });
        } catch {
          // non-fatal — settings screen shows empty state
        }
      },

      updateSettings: async (patch: Partial<SettingsConfig>) => {
        try {
          const res = await apiFetch(`${API_BASE}/api/settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(patch),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          set({ settings: data.settings as SettingsConfig });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      loadBurnRate: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/settings/burn-rate`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          set({ burnRate: data as BurnRateStatus });
        } catch {
          // non-fatal
        }
      },

      setDepartmentPause: async (department: string, paused: boolean) => {
        try {
          const endpoint = paused ? 'pause' : 'resume';
          const res = await apiFetch(`${API_BASE}/api/settings/departments/${encodeURIComponent(department)}/${endpoint}`, {
            method: 'POST',
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          set((s) => ({
            settings: s.settings ? { ...s.settings, department_pauses: data.settings.department_pauses } : null,
          }));
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      // ZPC CRUD
      loadBacklogEpics: async (status?: string) => {
        const url = status
          ? `${API_BASE}/api/zpc/backlog?status=${status}&limit=100`
          : `${API_BASE}/api/zpc/backlog?limit=100`;
        try {
          const res = await apiFetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: BacklogEpic[] = await res.json();
          set({ backlogEpics: data });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      createEpic: async (title: string, description: string, wsjf: number) => {
        const res = await apiFetch(`${API_BASE}/api/zpc/backlog`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title, description, wsjf }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(err.detail ?? 'Failed to create epic');
        }
        const epic: BacklogEpic = await res.json();
        set(s => ({ backlogEpics: [epic, ...s.backlogEpics] }));
        return epic;
      },

      updateEpicStatus: async (epicId: string, status: string) => {
        const res = await apiFetch(`${API_BASE}/api/zpc/backlog/${epicId}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(err.detail ?? 'Failed to update status');
        }
        const updated: BacklogEpic = await res.json();
        set(s => ({
          backlogEpics: s.backlogEpics.map(e => e.id === epicId ? updated : e),
        }));
      },

      loadArchFacts: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/arch-facts`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: ArchFact[] = await res.json();
          set({ archFacts: data });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      addArchFact: async (domain: string, key: string, value: string, confidence = 'inferred') => {
        const res = await apiFetch(`${API_BASE}/api/zpc/arch-facts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domain, key, value, confidence }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(err.detail ?? 'Failed to add architecture fact');
        }
        const fact: ArchFact = await res.json();
        set(s => ({
          archFacts: s.archFacts.filter(f => !(f.domain === domain && f.key === key)).concat(fact),
        }));
        return fact;
      },

      loadLucasChat: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/chat`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: LucasChatMessage[] = await res.json();
          set({ lucasChat: data });
        } catch {
          set({ lucasChat: [] });
        }
      },

      sendLucasMessage: async (message: string) => {
        const ts = Date.now();
        set(s => ({
          lucasChat: [...s.lucasChat, {
            id: `msg-${ts}`,
            role: 'founder',
            content: message,
            created_at: new Date().toISOString(),
          }],
          lucasChatLoading: true,
        }));
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          set(s => ({
            lucasChat: [...s.lucasChat.filter(m => m.id !== `msg-${ts}`), data.message, data.response],
            lucasChatLoading: false,
          }));
        } catch (error) {
          set({ error: (error as Error).message, lucasChatLoading: false });
        }
      },

      loadApprovals: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/approvals`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: ApprovalItem[] = await res.json();
          set({ approvals: data });
        } catch {
          set({ approvals: [] });
        }
      },

      decideApproval: async (nmId: string, decision: 'approved' | 'rejected', reason: string) => {
        const res = await apiFetch(`${API_BASE}/api/zpc/approvals/${nmId}/decide`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ decision, reason }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(err.detail ?? 'Failed to record decision');
        }
        set(s => ({
          approvals: s.approvals.map(a =>
            a.id === nmId ? { ...a, decision, reason, decided_at: new Date().toISOString() } : a
          ),
        }));
      },

      loadGovernorDecisions: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/governor-decisions?limit=20`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: GovernorDecisionItem[] = await res.json();
          set({ governorDecisions: data });
        } catch {
          set({ governorDecisions: [] });
        }
      },

      loadBoardDecisions: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/board-decisions?limit=50`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: BoardDecision[] = await res.json();
          set({ boardDecisions: data });
        } catch {
          set({ boardDecisions: [] });
        }
      },

      loadBoardPersonas: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/board/personas`);
          if (!res.ok) { set({ boardPersonas: [] }); return; }
          const data = await res.json() as { personas: BoardPersona[] };
          set({ boardPersonas: data.personas ?? [] });
        } catch {
          set({ boardPersonas: [] });
        }
      },

      setError: (error: string | undefined) => {
        set({ error });
      },

      clearError: () => {
        set({ error: undefined });
      },

      // ── Toast notifications ───────────────────────────────────────────────
      addToast: (message: string, type: Toast['type'] = 'success') => {
        const id = `toast-${Date.now()}`;
        set(s => ({ toasts: [...s.toasts, { id, message, type }] }));
        setTimeout(() => {
          set(s => ({ toasts: s.toasts.filter(t => t.id !== id) }));
        }, 3500);
      },

      removeToast: (id: string) => {
        set(s => ({ toasts: s.toasts.filter(t => t.id !== id) }));
      },

      // ── Briefing regeneration ─────────────────────────────────────────────
      triggerBriefingRegeneration: async () => {
        const { addToast } = get();
        try {
          const res = await apiFetch(`${API_BASE}/api/command-centre/briefing/regenerate`, {
            method: 'POST',
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          addToast('Morning briefing regeneration queued', 'success');
          await get().loadMatchReport();
        } catch (error) {
          addToast(`Briefing regeneration failed: ${(error as Error).message}`, 'error');
        }
      },

      // ── Mandate archetype ─────────────────────────────────────────────────
      loadMandateArchetype: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/mandate-archetype`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: MandateArchetype = await res.json();
          set({ mandateArchetype: data });
        } catch {
          // non-fatal
        }
      },

      setMandateArchetype: async (archetype: string) => {
        const prev = get().mandateArchetype;
        set({ mandateArchetype: { archetype, updated_at: new Date().toISOString() } });
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/mandate-archetype`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ archetype }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: MandateArchetype = await res.json();
          set({ mandateArchetype: data });
          get().addToast(`Mandate archetype set to: ${archetype}`, 'success');
        } catch (error) {
          set({ mandateArchetype: prev });
          get().addToast(`Failed to set archetype: ${(error as Error).message}`, 'error');
        }
      },

      // ── Skill blocking ────────────────────────────────────────────────────
      loadBlockedSkills: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/skills/blocked`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: BlockedSkill[] = await res.json();
          set({ blockedSkills: data });
        } catch {
          // non-fatal
        }
      },

      blockSkill: async (skillName: string, reason: string) => {
        const prev = get().blockedSkills;
        const optimistic: BlockedSkill = {
          skill_name: skillName,
          blocked_at: new Date().toISOString(),
          reason,
        };
        set(s => ({ blockedSkills: [...s.blockedSkills, optimistic] }));
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/skills/${encodeURIComponent(skillName)}/block`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reason }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: BlockedSkill = await res.json();
          set(s => ({
            blockedSkills: s.blockedSkills.map(sk =>
              sk.skill_name === skillName ? data : sk
            ),
          }));
          get().addToast(`Skill "${skillName}" blocked`, 'success');
        } catch (error) {
          set({ blockedSkills: prev });
          get().addToast(`Failed to block skill: ${(error as Error).message}`, 'error');
        }
      },

      unblockSkill: async (skillName: string) => {
        const prev = get().blockedSkills;
        set(s => ({ blockedSkills: s.blockedSkills.filter(sk => sk.skill_name !== skillName) }));
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/skills/${encodeURIComponent(skillName)}/block`, {
            method: 'DELETE',
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          get().addToast(`Skill "${skillName}" unblocked`, 'success');
        } catch (error) {
          set({ blockedSkills: prev });
          get().addToast(`Failed to unblock skill: ${(error as Error).message}`, 'error');
        }
      },

      // ── Alert thresholds ──────────────────────────────────────────────────
      loadAlertThresholds: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/settings/alert-thresholds`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: AlertThresholds = await res.json();
          set({ alertThresholds: data });
        } catch {
          // non-fatal
        }
      },

      setAlertThresholds: async (thresholds: AlertThresholds) => {
        const prev = get().alertThresholds;
        set({ alertThresholds: thresholds });
        try {
          const res = await apiFetch(`${API_BASE}/api/settings/alert-thresholds`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(thresholds),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data: AlertThresholds = await res.json();
          set({ alertThresholds: data });
          get().addToast('Alert thresholds saved', 'success');
        } catch (error) {
          set({ alertThresholds: prev });
          get().addToast(`Failed to save thresholds: ${(error as Error).message}`, 'error');
        }
      },

      // ── Discord notification level ────────────────────────────────────────
      loadDiscordLevel: async () => {
        try {
          const res = await apiFetch(`${API_BASE}/api/settings/discord-level`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          set({ discordLevel: data.level as DiscordLevel });
        } catch {
          // non-fatal
        }
      },

      setDiscordLevel: async (level: DiscordLevel) => {
        const prev = get().discordLevel;
        set({ discordLevel: level });
        try {
          const res = await apiFetch(`${API_BASE}/api/settings/discord-level`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ level }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          get().addToast(`Discord notifications set to: ${level}`, 'success');
        } catch (error) {
          set({ discordLevel: prev });
          get().addToast(`Failed to set Discord level: ${(error as Error).message}`, 'error');
        }
      },

      // ── Resolve board decision ────────────────────────────────────────────
      resolveBoardDecision: async (decisionId: string) => {
        const prev = get().boardDecisions;
        set(s => ({ boardDecisions: s.boardDecisions.filter(d => d.id !== decisionId) }));
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/board-decisions/${decisionId}/resolve`, {
            method: 'PATCH',
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          get().addToast('Board decision marked as resolved', 'success');
        } catch (error) {
          set({ boardDecisions: prev });
          get().addToast(`Failed to resolve decision: ${(error as Error).message}`, 'error');
        }
      },

      // ── Gate API write operations ─────────────────────────────────────────
      approveGateApi: async (gateId: string) => {
        const prev = get().gates;
        set(s => ({
          gates: s.gates.map(g => g.id === gateId ? { ...g, status: 'approved' as const } : g),
        }));
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/gates/${gateId}/approve`, {
            method: 'POST',
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          get().addToast('Gate approved — epic advances', 'success');
        } catch (error) {
          set({ gates: prev });
          get().addToast(`Failed to approve gate: ${(error as Error).message}`, 'error');
        }
      },

      rejectGateApi: async (gateId: string, reason: string) => {
        const prev = get().gates;
        set(s => ({
          gates: s.gates.map(g => g.id === gateId ? { ...g, status: 'rejected' as const } : g),
        }));
        try {
          const res = await apiFetch(`${API_BASE}/api/zpc/gates/${gateId}/reject`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reason }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          get().addToast('Gate rejected', 'info');
        } catch (error) {
          set({ gates: prev });
          get().addToast(`Failed to reject gate: ${(error as Error).message}`, 'error');
        }
      },

      // ── Founder interventions ─────────────────────────────────────────────
      applyIntervention: async (action: string, body: Record<string, unknown>) => {
        const res = await apiFetch(`${API_BASE}/api/zpc/interventions/${action}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const msg = (data as { error?: string }).error ?? `HTTP ${res.status}`;
          throw new Error(msg);
        }
        return data as Record<string, unknown>;
      },

      loadInterventionHistory: async (epicId?: string, actionType?: string) => {
        try {
          const params = new URLSearchParams();
          if (epicId) params.set('epic_id', epicId);
          if (actionType) params.set('action_type', actionType);
          const qs = params.toString();
          const res = await apiFetch(
            `${API_BASE}/api/zpc/interventions/history${qs ? '?' + qs : ''}`
          );
          if (!res.ok) return;
          const data: InterventionHistoryResponse = await res.json();
          set({ interventionHistory: data.items });
        } catch (_err) {
          // non-fatal — history is best-effort
        }
      },

      // Spatial UI (epic-371)
      focusDepartment: (deptId: string) => {
        set({ focusedDepartment: deptId });
      },
      unfocusDepartment: () => {
        set({ focusedDepartment: null, isOverlayOpen: false });
      },
      setSavedCameraPosition: (pos: { x: number; y: number; zoom: number }) => {
        set({ savedCameraPosition: pos });
      },
    }),
    { name: 'portal-store' }
  )
);

// Alias for compatibility
export const useStore = usePortalStore;
